import React, { useEffect, useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

function getOptionalAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (!sessionToken) return {};
  return { Authorization: `Bearer ${sessionToken}` };
}

function GenAiControl() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [triggerMatchId, setTriggerMatchId] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(null);

  const [oneLinerIntervalSec, setOneLinerIntervalSec] = useState('');
  const [matchSummaryIntervalSec, setMatchSummaryIntervalSec] = useState('');
  const [playerMomentsIntervalMin, setPlayerMomentsIntervalMin] = useState('');
  const [globalMaxJobs, setGlobalMaxJobs] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/admin/genai/status`, {
        method: 'GET',
        credentials: 'include',
        headers: getOptionalAuthHeaders(),
      });
      if (res.status === 401 || res.status === 403) {
        setError('Unauthorized. Please log in again.');
        setStatus(null);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Failed to load GenAI status');
        setStatus(null);
        return;
      }
      const raw = data.data ?? data;
      setStatus(raw);
      if (raw?.oneLineUpdates?.intervalSec != null) setOneLinerIntervalSec(String(raw.oneLineUpdates.intervalSec));
      if (raw?.matchSummary?.intervalSec != null) setMatchSummaryIntervalSec(String(raw.matchSummary.intervalSec));
      if (raw?.playerMoments?.intervalMin != null) setPlayerMomentsIntervalMin(String(raw.playerMoments.intervalMin));
      if (raw?.global?.maxJobsPerCycle != null) setGlobalMaxJobs(String(raw.global.maxJobsPerCycle));
    } catch (err) {
      setError(err?.message || 'Failed to load GenAI status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const sendControl = async (payload) => {
    setError('');
    setSuccess('');
    setActionLoading(payload.useCase + payload.action);
    try {
      const res = await fetch(`${BASE_URL}/admin/genai/control`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getOptionalAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Control request failed');
        return;
      }
      setSuccess('Setting updated. Takes effect within the next cron cycle.');
      fetchStatus();
    } catch (err) {
      setError(err?.message || 'Control request failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleManualTrigger = async (type) => {
    const matchId = triggerMatchId.trim();
    if (!matchId) {
      setError('Enter a Match ID');
      return;
    }
    const pathMap = {
      oneLineUpdate: 'one-line-update',
      matchSummary: 'match-summary',
      feedSummary: 'feed-summary',
      playerMoments: 'player-moments',
    };
    const path = pathMap[type];
    if (!path) return;
    setError('');
    setSuccess('');
    setTriggerLoading(type);
    try {
      const res = await fetch(`${BASE_URL}/admin/genai/trigger/${path}/${matchId}`, {
        method: 'POST',
        credentials: 'include',
        headers: getOptionalAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || `Trigger ${type} failed`);
        return;
      }
      setSuccess(`Triggered ${type} for match ${matchId}`);
    } catch (err) {
      setError(err?.message || 'Trigger failed');
    } finally {
      setTriggerLoading(null);
    }
  };

  const handleClearQueue = async () => {
    if (!window.confirm('Clear the entire GenAI job queue? This cannot be undone.')) return;
    setError('');
    setSuccess('');
    setActionLoading('clear-queue');
    try {
      const res = await fetch(`${BASE_URL}/admin/genai/clear-queue`, {
        method: 'POST',
        credentials: 'include',
        headers: getOptionalAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Clear queue failed');
        return;
      }
      setSuccess('Queue cleared.');
      fetchStatus();
    } catch (err) {
      setError(err?.message || 'Clear queue failed');
    } finally {
      setActionLoading(null);
    }
  };

  const global = status?.global ?? {};
  const oneLine = status?.oneLineUpdates ?? {};
  const matchSummary = status?.matchSummary ?? {};
  const feedSummary = status?.feedSummary ?? {};
  const playerMoments = status?.playerMoments ?? {};
  const fanStories = status?.fanStories ?? {};

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-6">
                <h3>GenAI Admin Control</h3>
              </div>
              <div className="col-sm-6">
                <Button variant="outlined" size="small" onClick={fetchStatus} disabled={loading}>
                  {loading ? <CircularProgress size={20} /> : 'Refresh status'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {loading && !status ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {/* Global */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Global
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={global.enabled ? 'Enabled' : 'Disabled'}
                            color={global.enabled ? 'success' : 'default'}
                          />
                          {global.queueLength != null && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Queue length: {global.queueLength} · Max jobs/cycle: {global.maxJobsPerCycle ?? '—'}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <TextField
                            size="small"
                            label="Max jobs per cycle"
                            type="number"
                            value={globalMaxJobs}
                            onChange={(e) => setGlobalMaxJobs(e.target.value)}
                            sx={{ width: 140 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={actionLoading === 'globalsetMaxJobs' || !globalMaxJobs}
                            onClick={() => sendControl({ useCase: 'global', action: 'setMaxJobs', value: Number(globalMaxJobs) })}
                          >
                            Apply
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* One-line updates */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          One-line match updates
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={oneLine.enabled ? 'Running' : 'Stopped'}
                            color={oneLine.enabled ? 'success' : 'default'}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Interval: {oneLine.intervalDisplay ?? oneLine.intervalSec ?? '—'} · Milestone: {oneLine.milestoneDetection ? 'On' : 'Off'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'oneLineUpdates', action: 'start' })}
                          >
                            Start
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'oneLineUpdates', action: 'stop' })}
                          >
                            Stop
                          </Button>
                          <TextField
                            size="small"
                            label="Interval (seconds)"
                            type="number"
                            value={oneLinerIntervalSec}
                            onChange={(e) => setOneLinerIntervalSec(e.target.value)}
                            sx={{ width: 120 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={actionLoading !== null || !oneLinerIntervalSec}
                            onClick={() =>
                              sendControl({
                                useCase: 'oneLineUpdates',
                                action: 'setInterval',
                                value: Number(oneLinerIntervalSec),
                              })
                            }
                          >
                            Set interval
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Match summary (2-4 sentence prose) */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Match summary
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={matchSummary.enabled ? 'Running' : 'Stopped'}
                            color={matchSummary.enabled ? 'success' : 'default'}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Interval: {matchSummary.intervalDisplay ?? `${matchSummary.intervalSec ?? '—'} s`} ·
                            {' '}Milestone: {matchSummary.milestoneDetection ? 'On' : 'Off'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {matchSummary.outputFormat ?? '2-4 sentence trigger-aware prose'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'matchSummary', action: 'start' })}
                          >
                            Start
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'matchSummary', action: 'stop' })}
                          >
                            Stop
                          </Button>
                          <TextField
                            size="small"
                            label="Interval (seconds)"
                            type="number"
                            value={matchSummaryIntervalSec}
                            onChange={(e) => setMatchSummaryIntervalSec(e.target.value)}
                            sx={{ width: 140 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={actionLoading !== null || !matchSummaryIntervalSec}
                            onClick={() =>
                              sendControl({
                                useCase: 'matchSummary',
                                action: 'setInterval',
                                value: Number(matchSummaryIntervalSec),
                              })
                            }
                          >
                            Set interval
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Feed summary */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Feed summary
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={feedSummary.enabled ? 'Running' : 'Stopped'}
                            color={feedSummary.enabled ? 'success' : 'default'}
                          />
                          {feedSummary.triggerRule && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {feedSummary.triggerRule}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'feedSummary', action: 'start' })}
                          >
                            Start
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'feedSummary', action: 'stop' })}
                          >
                            Stop
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Fan stories */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Fan Stories
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={fanStories.enabled ? 'Running' : 'Stopped'}
                            color={fanStories.enabled ? 'success' : 'default'}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {fanStories.threshold ?? '≥5 posts + ≥3 creators to publish'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'fanStories', action: 'start' })}
                          >
                            Start
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'fanStories', action: 'stop' })}
                          >
                            Stop
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Player moments */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Player moments
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={playerMoments.enabled ? 'Running' : 'Stopped'}
                            color={playerMoments.enabled ? 'success' : 'default'}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Interval: {playerMoments.intervalMin != null ? `${playerMoments.intervalMin} min` : '—'} · Auto on match complete: {playerMoments.autoTriggerOnMatchComplete ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'playerMoments', action: 'start' })}
                          >
                            Start
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={actionLoading !== null}
                            onClick={() => sendControl({ useCase: 'playerMoments', action: 'stop' })}
                          >
                            Stop
                          </Button>
                          <TextField
                            size="small"
                            label="Interval (minutes)"
                            type="number"
                            value={playerMomentsIntervalMin}
                            onChange={(e) => setPlayerMomentsIntervalMin(e.target.value)}
                            sx={{ width: 130 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={actionLoading !== null || !playerMomentsIntervalMin}
                            onClick={() =>
                              sendControl({
                                useCase: 'playerMoments',
                                action: 'setInterval',
                                value: Number(playerMomentsIntervalMin),
                              })
                            }
                          >
                            Set interval
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Manual triggers */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Manual triggers
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Run once for a specific match (same as cron would do).
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            label="Match ID"
                            type="number"
                            value={triggerMatchId}
                            onChange={(e) => setTriggerMatchId(e.target.value)}
                            placeholder="e.g. 12345"
                            sx={{ width: 140 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!triggerMatchId.trim() || triggerLoading !== null}
                            onClick={() => handleManualTrigger('oneLineUpdate')}
                          >
                            {triggerLoading === 'oneLineUpdate' ? <CircularProgress size={18} /> : 'One-line update'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!triggerMatchId.trim() || triggerLoading !== null}
                            onClick={() => handleManualTrigger('matchSummary')}
                          >
                            {triggerLoading === 'matchSummary' ? <CircularProgress size={18} /> : 'Match summary'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!triggerMatchId.trim() || triggerLoading !== null}
                            onClick={() => handleManualTrigger('feedSummary')}
                          >
                            {triggerLoading === 'feedSummary' ? <CircularProgress size={18} /> : 'Feed summary'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!triggerMatchId.trim() || triggerLoading !== null}
                            onClick={() => handleManualTrigger('playerMoments')}
                          >
                            {triggerLoading === 'playerMoments' ? <CircularProgress size={18} /> : 'Player moments'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Clear queue */}
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom color="error">
                          Emergency: clear queue
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Remove all pending GenAI jobs. Cannot be undone.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={actionLoading !== null}
                          onClick={handleClearQueue}
                        >
                          {actionLoading === 'clear-queue' ? <CircularProgress size={18} /> : 'Clear GenAI queue'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default GenAiControl;
